import { createSelector } from 'reselect';

import { createKeySelector } from 'Lib/reduxUtils';

/**
 * Gets context namespace from the state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {ContextState} entities by type
 */
export const selectContext = createKeySelector('context');

export const getIsRemote = createSelector(selectContext, (context) => !!context.remote);

/**
 * Gets the language from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} language
 */
export const getContextLanguage = createSelector(selectContext, (context) => context.lang);

/**
 * Gets the text direction from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} direction
 */
export const getDirection = createSelector(selectContext, (context) => context.dir);

/**
 * Gets the country from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} language
 */
export const getContextCountry = createSelector(selectContext, (context) => context.country);

/**
 * Gets the browser from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} language
 */
export const getBrowserFamily = createSelector(
  selectContext,
  (context) => context.browser?.family || ''
);

/**
 * Gets the device type from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} language
 */
export const getDeviceType = createSelector(selectContext, (context) => context.device?.type);

/**
 * Gets the OS from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} language
 */
export const getOsFamily = createSelector(selectContext, (context) => context.os?.family);

/**
 * Gets the formatted OS family and version number from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} language
 */
export const getOsFamilyAndVersion = createSelector(selectContext, (context) => {
  const { family = '', version = '0.0.0' } = context.os || {};
  return `${family} ${version}`;
});

/**
 * Determines if the OS is Android from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} language
 */
export const getIsOsFamilyAndroid = createSelector(getOsFamily, (family = '') =>
  family.toLowerCase().includes('android')
);

/**
 * Determines if the OS is Mac, iPad, or iOS from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} language
 */
export const getIsOsFamilyMacOrIos = createSelector(getOsFamily, (family = '') => {
  const fam = family.toLowerCase();
  return fam.includes('mac') || fam.includes('ios') || fam.includes('ipad');
});

/**
 * Gets the purchase ID in a 3ds transaction from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} pid
 */
export const getPid = createSelector(selectContext, (context) => context.pid);

/**
 * Gets the md in a 3ds transaction from the root state v4
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} md
 */
export const getMD = createSelector(selectContext, (context) => context.md);

/**
 * Gets the paResponse in a 3ds transaction from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} paRes
 */
export const getPaResponse = createSelector(selectContext, (context) => context.paRes);

/**
 * Gets the paResponse in a 3ds transaction from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Object} paRes
 */
export const getQueryParams = createSelector(selectContext, (context) => context.queryParams || {});

export const getRemoteApiToken = createSelector(
  selectContext,
  (context) => context.remoteApiToken || ''
);

export const getRemoteAppealToken = createSelector(
  selectContext,
  (context) => context.remoteAppealToken || ''
);

export const getRemoteComponent = createSelector(
  selectContext,
  (context) => context.component || ''
);

export const getRemoteSource = createSelector(
  selectContext,
  (context) => context.remoteSource || ''
);

export const getRemotePayload = createSelector(
  selectContext,
  (context) => context.remotePayload || ''
);

export const getExperience = createSelector(selectContext, (context) => context.experience);
